// Keep the picker hint and validation shared by both ways of selecting a timeline file.
export const TIMELINE_FILE_ACCEPT = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*,text/*,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.json,.xml';

export function getTimelineFileError(file: File): string {
    if (/^(audio|video)\//i.test(file.type) || /\.(mp3|m4a|wav|aac|ogg|webm|mp4|m4v|mov)$/i.test(file.name)) {
        return 'Use Import audio/video to add a recording to the timeline.';
    }
    if (file.size === 0) {
        return 'This file is empty. Choose another file.';
    }
    const supportedType = /^(image|text)\//i.test(file.type) || [
        'application/pdf', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(file.type.toLowerCase());
    const supportedName = /\.(pdf|doc|docx|jpg|jpeg|png|gif|webp|txt|csv|json|xml)$/i.test(file.name);
    return supportedType || supportedName ? '' : 'Choose a PDF, Word document, image or text file.';
}

// Give deliberate cancellation a distinct result so it never appears as an upload failure.
export function timelineUploadCancelled(): Error {
    const error = new Error('Upload cancelled.');
    error.name = 'AbortError';
    return error;
}

export function isTimelineUploadCancelled(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

export function getTimelineUploadError(error: unknown): string {
    const code = (error as { code?: string } | null)?.code;
    if (code === 'storage/unauthorized' || code === 'permission-denied') {
        return 'You do not have permission to upload to this timeline.';
    }
    if (code === 'storage/retry-limit-exceeded' || code === 'unavailable') {
        return 'The upload could not finish. Check your connection and try again.';
    }
    return error instanceof Error && !code ? error.message : 'Could not upload the file. Try again or choose another file.';
}


export const TIMELINE_MEDIA_ACCEPT = 'audio/*,video/*,.mp3,.m4a,.wav,.aac,.ogg,.webm,.mp4,.m4v,.mov';

// Some file managers omit MIME types; keep the existing media extension fallback consistent.
export function getTimelineMediaContentType(file: File): string {
    if (/^(audio|video)\//i.test(file.type)) return file.type.toLowerCase();
    if (file.type && file.type !== 'application/octet-stream') return file.type.toLowerCase();
    const types: Record<string, string> = {
        mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav', aac: 'audio/aac',
        ogg: 'audio/ogg', webm: 'video/webm', mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime',
    };
    return types[file.name.split('.').pop()?.toLowerCase() || ''] || 'application/octet-stream';
}

// Reject empty or non-media files before spending time on an import.
export function getTimelineMediaError(file: File): string {
    if (!file.size) return 'This file is empty. Choose another file.';
    return /^(audio|video)\//.test(getTimelineMediaContentType(file)) ? '' :
        'Choose an audio or video file. For documents and images, use Upload file.';
}
